// Local A/B experiment assignment — deterministic, pre-consent-safe.
//
// Assignment happens client-side at boot: the flow must branch on the very
// first screen render, long before the consent gate lets Statsig/Amplitude
// initialize (and in the EU, possibly never). The assigned arm is persisted in
// localStorage so a visitor keeps it across page loads, and can be forced with
// `?variant=<name>` for testing (e.g. ?variant=no_scan_treatment).
//
// Where the variant surfaces in analytics:
// - stamped on EVERY event as `ab_<experiment>` via the central track()
//   enricher (pre-consent buffered events carry it too, so the replay to
//   Amplitude/GA4/Meta is complete),
// - announced once per page load via an `experiment_assigned` event,
// - set as a user property at Amplitude/Statsig/GA4 init,
// - included in checkout metadata as `experiment_<name>` (same prefix
//   convention the backend already stores for Statsig exposures).
//
// Privacy: the stored assignment is a functional flag (flow consistency), not
// a pseudonymous identifier — same consent posture as the quiz answers the
// store already persists pre-consent. No analytics id is minted here.

import { track, registerEventEnricher } from '../app/analytics.js';

export type ExperimentName = 'face_scan';

interface ExperimentDefinition {
  /** Equal-weight arms; the variant name is what analytics dashboards show. */
  variants: readonly string[];
  /** Accepted ?variant= shorthands → canonical variant name. */
  aliases?: Record<string, string>;
}

export const EXPERIMENTS: Record<ExperimentName, ExperimentDefinition> = {
  // 50/50: full flow with the face-scan step vs the same flow without it
  // (selfie-preintro / selfie-intro / camera / analysis are gated in flow.ts).
  face_scan: {
    variants: ['with_scan_control', 'no_scan_treatment'],
    aliases: {
      with_scan: 'with_scan_control',
      control: 'with_scan_control',
      no_scan: 'no_scan_treatment',
      treatment: 'no_scan_treatment',
    },
  },
};

const STORAGE_KEY = 'lunai_ab_variants';

interface Assignment {
  variant: string;
  /**
   * true when the arm was ever set via ?variant= — persisted, so a tester's
   * forced session stays distinguishable from real 50/50 traffic on every
   * later visit (analysis excludes forced visitors via ab_<name>_forced).
   */
  forced: boolean;
}

// Memoized per page load — getVariant() must return a stable answer for the
// whole session (flow filtering runs on every navigation).
const assigned = new Map<ExperimentName, Assignment>();

function readStored(): Record<string, unknown> {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    // Guard against non-object junk (literal "null", arrays, strings).
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function storedAssignmentFor(experiment: ExperimentName): Assignment | null {
  const entry = readStored()[experiment];
  const raw = typeof entry === 'string'
    ? { variant: entry, forced: false } // pre-`forced` format
    : (entry as { variant?: unknown; forced?: unknown } | null);
  if (
    raw && typeof raw === 'object' && typeof raw.variant === 'string' &&
    EXPERIMENTS[experiment].variants.includes(raw.variant)
  ) {
    return { variant: raw.variant, forced: raw.forced === true };
  }
  return null;
}

function writeStored(experiment: ExperimentName, assignment: Assignment): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...readStored(),
      [experiment]: assignment,
    }));
  } catch {
    // localStorage unavailable (private mode) — assignment stays per-pageload.
  }
}

function overrideTokens(): string[] {
  const raw = new URLSearchParams(window.location.search).get('variant');
  if (!raw) return [];
  // Comma-separated so several experiments can be forced at once.
  return raw.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
}

function urlOverrideFor(experiment: ExperimentName): string | null {
  const def = EXPERIMENTS[experiment];
  for (const token of overrideTokens()) {
    if (def.variants.includes(token)) return token;
    // hasOwnProperty: ?variant=constructor must not resolve via Object.prototype
    if (def.aliases && Object.prototype.hasOwnProperty.call(def.aliases, token)) {
      return def.aliases[token];
    }
  }
  return null;
}

/**
 * Resolve the visitor's arm for an experiment.
 * Order: ?variant= URL override → stored assignment → random split.
 * The result is persisted and an `experiment_assigned` analytics event is
 * fired once per page load (buffered pre-consent, replayed to all sinks).
 */
export function getVariant(experiment: ExperimentName): string {
  const existing = assigned.get(experiment);
  if (existing) return existing.variant;

  const def = EXPERIMENTS[experiment];
  let assignment: Assignment;
  let source: 'url_override' | 'storage' | 'random';

  const override = urlOverrideFor(experiment);
  if (override) {
    assignment = { variant: override, forced: true };
    source = 'url_override';
  } else {
    const stored = storedAssignmentFor(experiment);
    if (stored) {
      assignment = stored;
      source = 'storage';
    } else {
      assignment = {
        variant: def.variants[Math.floor(Math.random() * def.variants.length)],
        forced: false,
      };
      source = 'random';
    }
  }

  assigned.set(experiment, assignment);
  writeStored(experiment, assignment);

  track('experiment_assigned', {
    experiment_name: experiment,
    variant: assignment.variant,
    assignment_source: source,
    forced: assignment.forced,
  });
  console.log(`🧪 Experiment ${experiment} → ${assignment.variant} (${source}${assignment.forced ? ', forced' : ''})`);

  return assignment.variant;
}

/** All experiments with their assigned arm: { face_scan: 'no_scan_treatment' } */
export function getActiveExperiments(): Record<string, string> {
  const result: Record<string, string> = {};
  for (const name of Object.keys(EXPERIMENTS) as ExperimentName[]) {
    result[name] = getVariant(name);
  }
  return result;
}

/**
 * Analytics-property form: { ab_face_scan: 'no_scan_treatment' }, plus
 * ab_<name>_forced: true when the arm came from a ?variant= override (ever) —
 * dashboards exclude forced testers from experiment readouts with it.
 */
export function getExperimentProperties(): Record<string, string | boolean> {
  const props: Record<string, string | boolean> = {};
  for (const [name, assignment] of assigned) {
    props[`ab_${name}`] = assignment.variant;
    if (assignment.forced) {
      props[`ab_${name}_forced`] = true;
    }
  }
  return props;
}

/**
 * Assign every experiment and start stamping `ab_<experiment>` on all
 * analytics events. Call once at boot, before initAnalytics() fires
 * session_start, so even the first buffered events carry the arm.
 */
export function initExperiments(): void {
  const unknown = overrideTokens().filter(token =>
    !Object.values(EXPERIMENTS).some(
      def => def.variants.includes(token) || def.aliases?.[token]
    )
  );
  if (unknown.length > 0) {
    console.warn(`🧪 Ignoring unknown ?variant= value(s): ${unknown.join(', ')}`);
  }

  // Snapshot-based (reads `assigned` directly, never forces assignment) so an
  // enricher run can't recursively re-enter getVariant mid-assignment.
  registerEventEnricher(getExperimentProperties);

  getActiveExperiments();
}

/** Screens can be gated to specific arms via a `variantGate` field in flow.ts. */
export interface VariantGate {
  experiment: ExperimentName;
  variants: string[];
}

export function isScreenEnabledForVariant(screen: { variantGate?: VariantGate }): boolean {
  const gate = screen.variantGate;
  if (!gate) return true;
  return gate.variants.includes(getVariant(gate.experiment));
}
